import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'
import NProgress from 'nprogress'
import { useAuthStore, useCurrentStore } from '@/stores'

const routes: RouteRecordRaw[] = [
    {
        path: '/',
        redirect: '/home'
    },

    {
        path: '/download',
        component: () => import('@/pages/download/Download.vue'),
        meta: {
            autorisationLevel: 0,
            layout: 'Centered'
        }
    },

    {
        path: '/liveness/:aId/:uId',
        component: () => import('@/pages/download/Liveness.vue'),
        meta: {
            autorisationLevel: 0,
            layout: 'Centered'
        }
    },

    {
        path: '/profile',
        name: 'profile',
        component: () => import('@/pages/user/Profile.vue'),
        meta: {
            autorisationLevel: 1,
            layout: 'Single'
        }
    },

    {
        path: '/profile/kyc',
        name: 'profile-kyc',
        component: () => import('@/pages/user/Kyc.vue'),
        meta: {
            autorisationLevel: 1,
            layout: 'Single'
        }
    },

    {
        // link to change agency
        path: '/agencies/:agencyId/:path*',
        redirect: (to) => {
            if (to.meta.requiresAuth && !useAuthStore().isLoggedIn) {
                return { path: '/sign-in', query: { redirect: to.fullPath } }
            }
            const path =
                '/' + (Array.isArray(to.params.path) ? (to.params.path as string[]).join('/') : '')
            useCurrentStore().setAgencyId(to.params.agencyId as string)
            return path
        }
    },

    {
        path: '/organisation/help',
        component: () => import('@ezio/ui/layouts/DashboardMain.vue'),
        meta: {
            layout: 'Organization',
            sidebar: false
        },
        children: [
            {
                path: '',
                name: 'organisation-help',
                component: () => import('@/pages/support/Help.vue')
            }
        ]
    },
    {
        path: '/organization/agencies',
        name: 'account-agencies',
        component: () => import('@ezio/ui/layouts/DashboardMain.vue'),
        meta: {
            layout: 'Organization'
        },
        children: [
            {
                path: '',
                name: 'agencies',
                components: {
                    default: () => import('@/pages/organization/agencies/Home.vue'),
                    secondarySidebar: () => import('@/pages/organization/agencies/Nav.vue')
                }
            },
            {
                path: 'create',
                name: 'agency-create',
                components: {
                    default: () => import('@/pages/organization/agencies/CreateEdit.vue'),
                    secondarySidebar: () => import('@/pages/organization/agencies/Nav.vue')
                }
            },
            {
                path: ':id',
                components: {
                    default: () => import('@ezio/ui/layouts/RouterViewWrapper.vue'),
                    secondarySidebar: () => import('@/pages/organization/agencies/Nav.vue')
                },
                children: [
                    {
                        path: '',
                        name: 'agency',
                        component: () => import('@/pages/organization/agencies/Show.vue')
                    },
                    {
                        path: 'accounting-settings',
                        name: 'accounting-settings',
                        components: {
                            default: () =>
                                import('@/pages/organization/agencies/settings/Accounting.vue'),
                            secondarySidebar: () => import('@/pages/organization/agencies/Nav.vue')
                        }
                    },
                    {
                        path: 'parameters',
                        name: 'parameters',
                        components: {
                            default: () =>
                                import('@/pages/organization/agencies/settings/Parameters.vue'),
                            secondarySidebar: () => import('@/pages/organization/agencies/Nav.vue')
                        }
                    },
                    {
                        path: 'exports-settings',
                        name: 'exports-settings',
                        components: {
                            default: () =>
                                import('@/pages/organization/agencies/settings/Export.vue'),
                            secondarySidebar: () => import('@/pages/organization/agencies/Nav.vue')
                        }
                    },
                    {
                        path: 'edit',
                        name: 'agency-edit',
                        components: {
                            default: () => import('@/pages/organization/agencies/CreateEdit.vue'),
                            secondarySidebar: () => import('@/pages/organization/agencies/Nav.vue')
                        }
                    },
                    {
                        path: 'kbis',
                        name: 'kbis',
                        component: () => import('@/pages/organization/agencies/Kbis.vue')
                    },
                    {
                        path: 'wire-transfer',
                        name: 'wire-transfer',
                        component: () => import('@/pages/organization/agencies/Wiretransfer.vue')
                    },
                    {
                        path: 'users/create',
                        name: 'agency-user-create',
                        component: () => import('@/pages/organization/agencies/users/Create.vue')
                    },
                    {
                        path: 'users/:userId/edit',
                        name: 'agency-user-edit',
                        component: () => import('@/pages/organization/agencies/users/Edit.vue')
                    }
                ]
            }
        ]
    },
    {
        path: '/organization/invoices',
        name: 'account-invoices',
        component: () => import('@ezio/ui/layouts/DashboardMain.vue'),
        meta: {
            layout: 'Organization'
        },
        children: [
            {
                path: '',
                name: 'organisation-invoices',
                components: {
                    default: () => import('@/pages/organization/invoices/Home.vue'),
                    secondarySidebar: () => import('@/pages/organization/invoices/Nav.vue')
                }
            }
        ]
    },

    {
        path: '/home',
        component: () => import('@ezio/ui/layouts/DashboardMain.vue'),
        children: [
            {
                path: '',
                name: 'home',
                component: () => import('@/pages/home/Home.vue'),
                meta: {
                    sidebar: false
                }
            }
        ]
    },

    {
        path: '/transactions',
        component: () => import('@ezio/ui/layouts/DashboardMain.vue'),
        children: [
            {
                path: '',
                name: 'transactions',
                component: () => import('@/pages/transactions/Index.vue'),
                meta: {
                    sidebar: false
                }
            },
            {
                path: ':id',
                name: 'transaction',
                component: () => import('@/pages/transactions/Show.vue'),
                meta: {
                    sidebar: false
                }
            }
        ]
    },
    {
        path: '/cards',
        component: () => import('@ezio/ui/layouts/DashboardMain.vue'),

        children: [
            {
                path: '',
                name: 'cards',
                components: {
                    default: () => import('@/pages/cards/Home.vue'),
                    secondarySidebar: () => import('@/pages/cards/Nav.vue')
                }
            },
            {
                path: ':id',
                components: {
                    default: () => import('@ezio/ui/layouts/RouterViewWrapper.vue'),
                    secondarySidebar: () => import('@/pages/cards/Nav.vue')
                },
                children: [
                    {
                        path: '',
                        name: 'card',
                        component: () => import('@/pages/cards/Show.vue')
                    }
                ]
            }
        ]
    },

    {
        path: '/clients',
        component: () => import('@ezio/ui/layouts/DashboardMain.vue'),

        children: [
            {
                path: '',
                name: 'clients',
                components: {
                    default: () => import('@/pages/clients/Home.vue'),
                    secondarySidebar: () => import('@/pages/clients/Nav.vue')
                }
            },
            {
                path: 'create',
                name: 'client-create',
                components: {
                    default: () => import('@/pages/clients/CreateEdit.vue'),
                    secondarySidebar: () => import('@/pages/clients/Nav.vue')
                }
            },
            {
                path: ':id',
                components: {
                    default: () => import('@ezio/ui/layouts/RouterViewWrapper.vue'),
                    secondarySidebar: () => import('@/pages/clients/Nav.vue')
                },
                children: [
                    {
                        path: '',
                        name: 'client',
                        component: () => import('@/pages/clients/Show.vue')
                    },
                    {
                        path: 'edit',
                        name: 'client-edit',
                        component: () => import('@/pages/clients/CreateEdit.vue')
                    },
                    {
                        path: 'mandate',
                        name: 'client-mandate',
                        component: () => import('@/pages/clients/operations/mandate/Show.vue')
                    },
                    {
                        path: 'mandate/create',
                        name: 'mandate-create',
                        component: () => import('@/pages/clients/operations/mandate/Create.vue')
                    },
                    {
                        path: 'deposit',
                        name: 'client-deposit',
                        component: () => import('@/pages/clients/operations/Deposit.vue')
                    },
                    {
                        path: 'deposits/refund',
                        name: 'client-refund',
                        component: () => import('@/pages/clients/operations/Refund.vue')
                    },
                    {
                        path: 'guests/create',
                        name: 'client-guest-create',
                        component: () => import('@/pages/clients/guests/CreateEdit.vue')
                    },
                    {
                        path: 'guests/:guestId/edit',
                        name: 'client-guests-edit',
                        component: () => import('@/pages/clients/guests/CreateEdit.vue')
                    }
                ]
            }
        ]
    },

    {
        path: '/employees',
        component: () => import('@ezio/ui/layouts/DashboardMain.vue'),

        children: [
            {
                path: '',
                name: 'employees',
                components: {
                    default: () => import('@/pages/employees/Home.vue'),
                    secondarySidebar: () => import('@/pages/employees/Nav.vue')
                }
            },
            {
                path: 'create',
                name: 'employee-create',
                components: {
                    default: () => import('@/pages/employees/CreateEdit.vue'),
                    secondarySidebar: () => import('@/pages/employees/Nav.vue')
                }
            },
            {
                path: ':id',
                components: {
                    default: () => import('@ezio/ui/layouts/RouterViewWrapper.vue'),
                    secondarySidebar: () => import('@/pages/employees/Nav.vue')
                },
                children: [
                    {
                        path: '',
                        name: 'employee',
                        component: () => import('@/pages/employees/Show.vue')
                    },
                    {
                        path: 'edit',
                        name: 'employee-edit',
                        components: {
                            default: () => import('@/pages/employees/CreateEdit.vue'),
                            secondarySidebar: () => import('@/pages/employees/Nav.vue')
                        }
                    }
                ]
            }
        ]
    },
    {
        path: '/bank',
        redirect: '/bank/bank-operations',
        component: () => import('@ezio/ui/layouts/DashboardMain.vue'),
        children: [
            {
                path: 'bank-operations',
                name: 'bank-operations',
                components: {
                    default: () => import('@/pages/bank-account/BankOperations.vue'),
                    secondarySidebar: () => import('@/pages/bank-account/Nav.vue')
                }
            },
            {
                path: 'bank-account',
                name: 'bank-account',
                components: {
                    default: () => import('@/pages/bank-account/BankAccount.vue'),
                    secondarySidebar: () => import('@/pages/bank-account/Nav.vue')
                }
            },
            {
                path: 'bank-reports',
                name: 'bank-reports',
                components: {
                    default: () => import('@/pages/bank-account/BankReports.vue'),
                    secondarySidebar: () => import('@/pages/bank-account/Nav.vue')
                }
            },
            {
                path: 'bank-payins',
                name: 'bank-payins',
                components: {
                    default: () => import('@/pages/bank-account/BankPayins.vue'),
                    secondarySidebar: () => import('@/pages/bank-account/Nav.vue')
                }
            }
        ]
    },

    {
        path: '/help',
        component: () => import('@ezio/ui/layouts/DashboardMain.vue'),
        meta: { sidebar: false },
        children: [
            {
                path: '',
                name: 'help',
                component: () => import('@/pages/support/Help.vue')
            }
        ]
    },
    {
        path: '/sign-in',
        name: 'signIn',
        component: () => import('@/pages/auth/SignIn.vue'),
        meta: {
            layout: 'Centered',
            autorisationLevel: 0
        }
    },
    {
        path: '/sign-up',
        name: 'signUp',
        component: () => import('@/pages/auth/SignUp.vue'),
        meta: {
            layout: 'Centered',
            autorisationLevel: 0
        }
    },
    {
        path: '/password-reset',
        name: 'password-reset',
        component: () => import('@/pages/auth/PasswordReset.vue'),
        meta: {
            layout: 'Centered',
            autorisationLevel: 0
        }
    },
    {
        path: '/password-change',
        name: 'password-change',
        component: () => import('@/pages/auth/PasswordChange.vue'),
        meta: {
            layout: 'Centered',
            autorisationLevel: 0
        }
    },
    {
        path: '/managers/sign-up',
        name: 'managers-signup',
        component: () => import('@/pages/managers/SignUp.vue'),
        meta: {
            layout: 'Centered',
            autorisationLevel: 0
        }
    }
]

const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    routes
})

router.beforeEach(async (to, from) => {
    if (to.path === '/espace-client/') {
        window.location.replace('https://client-space.ezio.app')
    }

    if (!NProgress.isStarted()) {
        NProgress.start()
    }

    const currentStore = useCurrentStore()

    if (useAuthStore().isLoggedIn && !currentStore.user) {
        await currentStore.fetch()
    }

    console.log('level', useAuthStore().autorisationLevel)
    const requiredAutorisationLevel = (to.meta.autorisationLevel as number) ?? 9
    const userAutorisationLevel = useAuthStore().autorisationLevel

    if (userAutorisationLevel < requiredAutorisationLevel) {
        if (userAutorisationLevel === 0) {
            return '/sign-in?redirect=' + to.fullPath
        } else if (userAutorisationLevel === 1) {
            return '/profile'
        } else {
            return false
        }
    }
    return true
})

router.afterEach(() => {
    NProgress.done()
})

export default router
